'use client';

import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Zap, ArrowRight, RefreshCw } from 'lucide-react';
import { useAuth } from '@/providers/auth-provider';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/components/ui/sonner';
import { motion } from 'framer-motion';

export function VerifyEmailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { verifyEmail } = useAuth();
  
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (token) {
      verifyEmailToken();
    }
  }, [token]);

  const verifyEmailToken = async () => {
    setStatus('loading');
    try {
      await verifyEmail(token!);
      setStatus('success');
      setMessage('Your email has been verified successfully!');
      toast.success('Email verified!', { description: 'Redirecting to dashboard...' });
      setTimeout(() => navigate('/'), 2000);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Verification failed';
      if (msg.includes('expired') || msg.includes('invalid')) {
        setStatus('error');
        setMessage('This verification link has expired or is invalid. Please request a new one.');
      } else {
        setStatus('error');
        setMessage(msg);
      }
      toast.error('Verification failed', { description: msg });
    }
  };

  const handleResend = async () => {
    toast.info('Please log in to resend the verification email');
    navigate('/login');
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md text-center"
        >
          <Card className="glass-strong">
            <CardContent className="pt-6 pb-8 px-6">
              <div className="flex justify-center mb-4">
                <div className="relative flex h-16 w-16">
                  <div className="animate-spin absolute inset-0 rounded-full border-4 border-slate-700" />
                  <div className="absolute inset-0 rounded-full border-4 border-cyan-500 border-t-transparent" />
                </div>
              </div>
              <CardTitle className="text-2xl">Verifying your email...</CardTitle>
              <CardDescription className="text-slate-400">Please wait while we verify your account</CardDescription>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Card className="glass-strong">
          <CardHeader className="text-center">
            <Link to="/" className="inline-flex items-center justify-center mb-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-500 to-purple-500">
                <Zap className="h-7 w-7 text-white" />
              </div>
            </Link>
            {status === 'success' ? (
              <>
                <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-4" />
                <CardTitle className="text-2xl">Email Verified!</CardTitle>
                <CardDescription className="text-slate-400">{message}</CardDescription>
              </>
            ) : (
              <>
                <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
                <CardTitle className="text-2xl">Verification Failed</CardTitle>
                <CardDescription className="text-slate-400">{message}</CardDescription>
              </>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {status === 'error' && (
              <Button variant="outline" onClick={handleResend} className="w-full">
                <RefreshCw className="mr-2 h-4 w-4" />
                Resend Verification Email
              </Button>
            )}
            <Button onClick={() => navigate('/login')} className="w-full" variant={status === 'success' ? 'default' : 'outline'}>
              <ArrowRight className="mr-2 h-4 w-4" />
              {status === 'success' ? 'Go to Dashboard' : 'Back to Login'}
            </Button>
          </CardContent>
          <CardFooter className="flex flex-col gap-4">
            <Separator className="w-full" />
            <p className="text-center text-sm text-slate-400">
              Need help?{' '}
              <Link to="/support" className="text-cyan-400 hover:underline">Contact Support</Link>
            </p>
          </CardFooter>
        </Card>

        <p className="text-center text-xs text-slate-500 mt-6">
          By verifying your email, you agree to our{' '}
          <Link to="/terms" className="text-cyan-400 hover:underline">Terms of Service</Link>{' '}
          and{' '}
          <Link to="/privacy" className="text-cyan-400 hover:underline">Privacy Policy</Link>
        </p>
      </motion.div>
    </div>
  );
}
